import { useState, useRef, KeyboardEvent } from "react";
import styled from "styled-components";
import { CorporateTheme, font } from "../../utils/themes";
import SearchIcon from "../../assets/icons/SearchIcon";
import CloseIcon from "../../assets/icons/CloseIcon";
import { useTheme } from "../../middleware/ThemeContext";
import StyledText from "../StyledComponents/StyledText";

const SearchContainer = styled("div")<{ $isExpanded: boolean, disabled?: boolean }>(
  ({ theme, $isExpanded, disabled }) => ({
    display: "flex",
    alignItems: "center",
    transition: "all 0.3s ease",
    // background: theme.CorporateTheme.colors.background,
    gap: CorporateTheme.spacing.sm,
    width: $isExpanded ? "360px" : "auto",
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? '0.5' : '1',
    borderBottom: $isExpanded
      ? `1px solid ${theme.CorporateTheme.colors.textSecondary}`
      : "none",
    boxShadow: $isExpanded
      ? `0px 1px 0px 0px ${theme.CorporateTheme.colors.textSecondary}`
      : "none",
    maxHeight: '39px',
  })
);

const SearchButton = styled("button")(({ theme, disabled }) => ({
  display: "flex",
  alignItems: "center",
  gap: CorporateTheme.spacing.sm,
  border: "none",
  background: "none",
  padding: `${CorporateTheme.spacing.sm} ${CorporateTheme.spacing.sm}`,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? '0.5' : '1',
  color: theme?.CorporateTheme?.colors.textSecondary,
  fontSize: "14px",
  fontFamily: "'Raleway', sans-serif",
  fontWeight: 500,
  transition: "all 0.2s",
  borderRadius: CorporateTheme.borderRadius,

  "&:hover": {
    color: theme?.CorporateTheme?.colors?.accent,
    background: disabled ? 'transparent' : theme?.CorporateTheme?.colors?.filterBG,
  },
}));

export const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.sm};
  width: 100%;
  padding: 13px ${CorporateTheme.spacing.sm};
`;

export const StyledInput = styled("input")(({ theme }) => ({
  flex: 1,
  border: "none",
  outline: "none",
  fontSize: "0.875rem",
  padding: `${CorporateTheme.spacing.xs} 0`,
  background: "transparent",
  color: theme?.CorporateTheme?.colors?.textSecondary,
  fontWeight: 500,
  fontFamily: font.raleway,
  "::placeholder": {
    color: theme?.CorporateTheme?.colors?.textSecondary,
  },
}));

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${CorporateTheme?.colors?.textSecondary};

  &:hover {
    color: ${CorporateTheme?.colors?.danger};
  }
`;

interface ExpandableSearchProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean
}

const ExpandableSearch = ({ value, onChange, disabled }: ExpandableSearchProps) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(value?.length > 0 ? true : false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchClick = () => {
    if (disabled) return
    setIsExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleClose = () => {
    if (value) {
      onChange({
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    setTimeout(() => inputRef.current?.blur(), 0);
    setIsExpanded(false);
  };

  return (
    <SearchContainer $isExpanded={isExpanded} theme={theme} disabled={disabled}>
      {!isExpanded ? (
        <SearchButton onClick={handleSearchClick} theme={theme} disabled={disabled}>
          <SearchIcon color={theme.CorporateTheme?.colors?.headerColor} />
          <StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Search</StyledText>
        </SearchButton>
      ) : (
        <SearchInputWrapper>
          <SearchIcon color={theme.CorporateTheme?.colors?.textSecondary} />
          <StyledInput
            theme={theme}
            ref={inputRef}
            type="text"
            placeholder="Search"
            value={value}
            onChange={onChange}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Escape") {
                handleClose();
              }
            }}
          />
          <CloseButton onClick={handleClose}>
            <CloseIcon color={theme.CorporateTheme?.colors?.textSecondary} />
          </CloseButton>
        </SearchInputWrapper>
      )}
    </SearchContainer>
  );
};

export default ExpandableSearch;
